import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { Plus, School, Upload, Download, ListChecks, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];
const emptyCreateForm = { code: '', name: '', phase_id: '', district_id: '', contract_value: '' };
const emptyInstallmentRow = () => ({ planned_amount: '', due_date: '' });

// One row per installment — a common spreadsheet shape. Rows sharing an
// Institute Code are grouped client-side before import: the first
// occurrence's institute fields create the institution, and every row in
// the group becomes one installment on it, posted together in a single
// POST /institutions call per institute (installments are created
// atomically with their institution, not row-by-row).
const TEMPLATE_HEADERS = ['Institute Code', 'Institute Name', 'Phase Code', 'District Code', 'Contract Value', 'Installment Number', 'Installment Amount', 'Due Date'];

const BulkUploadModal = ({ phases, districts, onClose, onDone }) => {
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const phaseByCode = new Map(phases.map(p => [String(p.code).trim().toUpperCase(), p]));
  const districtByCode = new Map(districts.map(d => [String(d.code).trim().toUpperCase(), d]));

  const downloadTemplate = () => {
    const phaseCode = phases[0]?.code || 'PH1';
    const districtCode = districts[0]?.code || '';
    const example = [
      { 'Institute Code': 'INST-001', 'Institute Name': 'Example Training Institute', 'Phase Code': phaseCode, 'District Code': districtCode, 'Contract Value': 500000, 'Installment Number': 1, 'Installment Amount': 250000, 'Due Date': '' },
      { 'Institute Code': 'INST-001', 'Institute Name': 'Example Training Institute', 'Phase Code': phaseCode, 'District Code': districtCode, 'Contract Value': 500000, 'Installment Number': 2, 'Installment Amount': 250000, 'Due Date': '' },
    ];
    const ws = XLSX.utils.json_to_sheet(example, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Institutions');
    XLSX.writeFile(wb, 'institution-template.xlsx');
  };

  const validateRow = (row) => {
    const errors = [];
    const code = String(row['Institute Code'] || '').trim();
    if (!code) errors.push('Institute Code missing');

    const name = String(row['Institute Name'] || '').trim();
    if (!name) errors.push('Institute Name missing');

    const phaseCode = String(row['Phase Code'] || '').trim().toUpperCase();
    const phase = phaseByCode.get(phaseCode);
    if (!phaseCode) errors.push('Phase Code missing');
    else if (!phase) errors.push(`Unknown Phase Code "${row['Phase Code']}"`);

    const districtCode = String(row['District Code'] || '').trim().toUpperCase();
    const district = districtCode ? districtByCode.get(districtCode) : null;
    if (districtCode && !district) errors.push(`Unknown District Code "${row['District Code']}"`);

    const contractValue = Number(row['Contract Value']);
    if (!row['Contract Value'] || !(contractValue > 0)) errors.push('Contract Value missing/invalid');

    const installmentNumber = Number(row['Installment Number']);
    if (!row['Installment Number'] || !Number.isInteger(installmentNumber) || installmentNumber < 1) errors.push('Installment Number missing/invalid');

    const installmentAmount = Number(row['Installment Amount']);
    if (!row['Installment Amount'] || !(installmentAmount > 0)) errors.push('Installment Amount missing/invalid');

    return {
      raw: row,
      valid: errors.length === 0,
      errors,
      code,
      fields: errors.length === 0 ? { name, phase_id: phase.id, district_id: district ? district.id : null, contract_value: contractValue } : null,
      installment: errors.length === 0 ? {
        installment_number: installmentNumber, planned_amount: installmentAmount, due_date: row['Due Date'] ? String(row['Due Date']).trim() : null,
      } : null,
    };
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (json.length === 0) {
        toast.error('No rows found in the file');
        setRows(null);
        return;
      }
      setRows(json.map(validateRow));
    } catch (err) {
      toast.error('Could not read that file — is it a valid .xlsx?');
      setRows(null);
    }
  };

  // Groups valid rows by Institute Code — the first row for a code
  // supplies the institute's own fields (name/phase/district/contract
  // value); every row in the group (including that first one) becomes one
  // installment.
  const groupByInstitute = (validRows) => {
    const groups = new Map();
    for (const r of validRows) {
      if (!groups.has(r.code)) groups.set(r.code, { code: r.code, ...r.fields, installments: [] });
      groups.get(r.code).installments.push(r.installment);
    }
    return [...groups.values()];
  };

  const handleImport = async () => {
    const validRows = rows.filter(r => r.valid);
    if (validRows.length === 0) return toast.error('No valid rows to import');
    const groups = groupByInstitute(validRows);
    setImporting(true);
    const outcomes = [];
    for (const g of groups) {
      try {
        await accountsApi.post('/institutions', g);
        outcomes.push({ ok: true, code: g.code });
      } catch (err) {
        outcomes.push({ ok: false, code: g.code, message: err.response?.data?.message || 'Failed to create' });
      }
    }
    setImporting(false);
    const successCount = outcomes.filter(o => o.ok).length;
    setResults({ successCount, failCount: outcomes.length - successCount, outcomes });
    if (successCount > 0) {
      toast.success(`${successCount} institution(s) imported`);
      onDone();
    }
    if (outcomes.some(o => !o.ok)) {
      toast.error(`${outcomes.length - successCount} institution(s) failed`);
    }
  };

  const validCount = rows ? rows.filter(r => r.valid).length : 0;
  const invalidCount = rows ? rows.length - validCount : 0;
  const groupCount = rows ? new Set(rows.filter(r => r.valid).map(r => r.code)).size : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2"><Upload size={18} /> Bulk Upload Institutions</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex items-end gap-3">
            <button type="button" onClick={downloadTemplate}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              <Download size={16} /> Download Template
            </button>
            <p className="text-xs text-gray-500">One row per installment — repeat the Institute Code for each tranche. Phase Code required, District Code optional.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Upload filled-in template (.xlsx)</label>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFile}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-800 file:text-white file:text-sm hover:file:bg-blue-900" />
          </div>

          {rows && (
            <>
              <div className="flex gap-4 text-sm">
                <span className="text-green-700 font-medium">{validCount} valid row(s) across {groupCount} institution(s)</span>
                {invalidCount > 0 && <span className="text-red-600 font-medium">{invalidCount} with errors (won't be imported)</span>}
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Row</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Institute Code</th>
                      <th className="text-center px-3 py-2 text-gray-600 font-semibold">Installment #</th>
                      <th className="text-right px-3 py-2 text-gray-600 font-semibold">Amount</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${r.valid ? '' : 'bg-red-50'}`}>
                        <td className="px-3 py-1.5 text-gray-500">{i + 2}</td>
                        <td className="px-3 py-1.5 text-gray-700">{r.raw['Institute Code']}</td>
                        <td className="px-3 py-1.5 text-center text-gray-700">{r.raw['Installment Number']}</td>
                        <td className="px-3 py-1.5 text-right text-gray-700">{r.raw['Installment Amount']}</td>
                        <td className={`px-3 py-1.5 ${r.valid ? 'text-green-700' : 'text-red-600'}`}>
                          {r.valid ? 'Valid' : r.errors.join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {results && (
            <div className={`rounded-lg p-3 text-sm ${results.failCount === 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}>
              Imported {results.successCount} of {results.successCount + results.failCount} institution(s).
              {results.failCount > 0 && ` Failed: ${results.outcomes.filter(o => !o.ok).map(o => `${o.code} (${o.message})`).join(', ')}`}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
          <button type="button" onClick={handleImport} disabled={!rows || validCount === 0 || importing}
            className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
            {importing ? 'Importing...' : `Import ${groupCount} Institution(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

const InstallmentsPanel = ({ institution, isFinance, onClose }) => {
  const [installments, setInstallments] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ installment_number: '', planned_amount: '', due_date: '' });

  const fetchInstallments = async () => {
    try {
      const res = await accountsApi.get(`/institutions/${institution.id}/installments`);
      setInstallments(res.data.installments);
    } catch (err) {
      toast.error('Failed to load installments');
    }
  };

  useEffect(() => { fetchInstallments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPlanned = installments ? installments.reduce((s, i) => s + Number(i.planned_amount), 0) : 0;
  const remaining = Number(institution.contract_value) - totalPlanned;

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await accountsApi.post(`/institutions/${institution.id}/installments`, {
        installment_number: Number(newRow.installment_number),
        planned_amount: Number(newRow.planned_amount),
        due_date: newRow.due_date || null,
      });
      toast.success('Installment added');
      setNewRow({ installment_number: '', planned_amount: '', due_date: '' });
      fetchInstallments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add installment');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <ListChecks size={18} /> {institution.code} — Installment Schedule
          </h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <p className="text-xs text-gray-500">
            Contract Value {Number(institution.contract_value).toFixed(2)} | Planned so far {totalPlanned.toFixed(2)}
            {' | '}<span className={remaining !== 0 ? 'text-amber-600' : 'text-green-700'}>
              {remaining === 0 ? 'Fully allocated' : `${remaining > 0 ? 'Unallocated' : 'Over-allocated'}: ${Math.abs(remaining).toFixed(2)}`}
            </span>
          </p>

          {installments === null ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
          ) : installments.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No installments defined yet</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-center px-3 py-2 text-gray-600 font-semibold">#</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Planned Amount</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Due Date</th>
                    <th className="text-center px-3 py-2 text-gray-600 font-semibold">Status</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Voucher</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map(inst => (
                    <tr key={inst.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-center text-gray-700">{inst.installment_number}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(inst.planned_amount).toFixed(2)}</td>
                      <td className="px-3 py-2 text-gray-600">{inst.due_date ? inst.due_date.slice(0, 10) : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${inst.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {inst.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {inst.voucher_id ? <Link to={`/vouchers/${inst.voucher_id}`} className="text-blue-700 hover:underline">{inst.voucher_no}</Link> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {isFinance && (
            <form onSubmit={handleAdd} className="flex items-end gap-2 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">#</label>
                <input required type="number" min="1" value={newRow.installment_number}
                  onChange={(e) => setNewRow({ ...newRow, installment_number: e.target.value })}
                  className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Planned Amount</label>
                <input required type="number" step="0.01" min="0.01" value={newRow.planned_amount}
                  onChange={(e) => setNewRow({ ...newRow, planned_amount: e.target.value })}
                  className="w-32 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Due Date (optional)</label>
                <input type="date" value={newRow.due_date}
                  onChange={(e) => setNewRow({ ...newRow, due_date: e.target.value })}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <button type="submit" disabled={adding}
                className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                {adding ? 'Adding...' : 'Add Installment'}
              </button>
            </form>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
};

const Institutions = () => {
  const { user } = useAuth();
  const isFinance = FINANCE_ROLES.includes(user?.role);

  const [institutions, setInstitutions] = useState([]);
  const [phases, setPhases] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [installmentRows, setInstallmentRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState('');
  const [viewingInstitution, setViewingInstitution] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [instRes, phasesRes, districtsRes] = await Promise.all([
        accountsApi.get('/institutions'),
        accountsApi.get('/phases'),
        accountsApi.get('/districts'),
      ]);
      setInstitutions(instRes.data.institutions);
      setPhases(phasesRes.data.phases);
      setDistricts(districtsRes.data.districts);
    } catch (err) {
      toast.error('Failed to load institutions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredInstitutions = phaseFilter
    ? institutions.filter(i => String(i.phase_id) === String(phaseFilter))
    : institutions;

  const totalAllocated = installmentRows.reduce((s, r) => s + (Number(r.planned_amount) || 0), 0);
  const remainingToAllocate = (Number(form.contract_value) || 0) - totalAllocated;

  const addInstallmentRow = () => setInstallmentRows(prev => [...prev, emptyInstallmentRow()]);
  const removeInstallmentRow = (index) => setInstallmentRows(prev => prev.filter((_, i) => i !== index));
  const updateInstallmentRow = (index, field, value) =>
    setInstallmentRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const installments = installmentRows
        .filter(r => Number(r.planned_amount) > 0)
        .map((r, idx) => ({ installment_number: idx + 1, planned_amount: Number(r.planned_amount), due_date: r.due_date || null }));
      await accountsApi.post('/institutions', {
        code: form.code, name: form.name, phase_id: Number(form.phase_id),
        district_id: form.district_id || null, contract_value: Number(form.contract_value),
        installments,
      });
      toast.success('Institution created');
      setShowCreateModal(false);
      setForm(emptyCreateForm);
      setInstallmentRows([]);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create institution');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Institutions">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500 max-w-2xl">
          The Procurement-supplied list of institutes eligible for payment, each under a Phase with its own contract value and installment schedule.
          A voucher can only pay an institute on this list against one of its installments, and posting is blocked once that installment is already paid or the amount would exceed it.
        </p>
        {isFinance && (
          <div className="flex gap-2 shrink-0 ml-4">
            <button onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 border border-blue-800 text-blue-800 px-4 py-2 rounded-lg text-sm hover:bg-blue-50">
              <Upload size={16} /> Bulk Upload
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
              <Plus size={16} /> New Institution
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phase</label>
            <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[16rem]">
              <option value="">All Phases</option>
              {phases.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <p className="text-xs text-gray-500 pb-2">
            {phaseFilter ? `Showing institutes under this phase only.` : 'Each Phase started a new year and holds its own set of institutes and districts.'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Phase</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Contract Value</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Installments</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filteredInstitutions.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No institutions {phaseFilter ? 'under this phase' : 'yet'}</td></tr>
            ) : filteredInstitutions.map(i => (
              <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{i.code}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{i.name}</td>
                <td className="px-4 py-3 text-gray-600">{i.phase_code} — {i.phase_name}</td>
                <td className="px-4 py-3 text-gray-600">{i.district_code ? `${i.district_code} — ${i.district_name}` : '—'}</td>
                <td className="px-4 py-3 text-right text-gray-800">{Number(i.contract_value).toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${i.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {i.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => setViewingInstitution(i)} className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1">
                    <ListChecks size={14} /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-bold flex items-center gap-2"><School size={18} /> New Institution</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                <input required type="text" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. INST-001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input required type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phase</label>
                  <select required value={form.phase_id} onChange={(e) => setForm({ ...form, phase_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select phase...</option>
                    {phases.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">District (optional)</label>
                  <select value={form.district_id} onChange={(e) => setForm({ ...form, district_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">None</option>
                    {districts.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contract Value</label>
                <input required type="number" step="0.01" min="0.01" value={form.contract_value}
                  onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-700">Installment Schedule (optional, can also be added later)</label>
                  <button type="button" onClick={addInstallmentRow} className="text-blue-700 hover:text-blue-900 text-xs font-medium">+ Add Installment</button>
                </div>
                {installmentRows.map((row, i) => (
                  <div key={i} className="flex items-end gap-2 mb-2">
                    <span className="text-xs text-gray-500 w-5 pb-2">{i + 1}</span>
                    <input type="number" step="0.01" min="0.01" placeholder="Amount" value={row.planned_amount}
                      onChange={(e) => updateInstallmentRow(i, 'planned_amount', e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <input type="date" value={row.due_date}
                      onChange={(e) => updateInstallmentRow(i, 'due_date', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button type="button" onClick={() => removeInstallmentRow(i)} className="text-red-500 hover:text-red-700 p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {installmentRows.length > 0 && (
                  <p className={`text-xs mt-1 ${remainingToAllocate !== 0 ? 'text-amber-600' : 'text-green-700'}`}>
                    {remainingToAllocate === 0 ? 'Fully allocated to the contract value.' : `${remainingToAllocate > 0 ? 'Unallocated' : 'Over-allocated'}: ${Math.abs(remainingToAllocate).toFixed(2)} (not blocking — just a nudge)`}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkUploadModal
          phases={phases} districts={districts}
          onClose={() => setShowBulkModal(false)}
          onDone={fetchAll}
        />
      )}

      {viewingInstitution && (
        <InstallmentsPanel institution={viewingInstitution} isFinance={isFinance} onClose={() => setViewingInstitution(null)} />
      )}
    </Layout>
  );
};

export default Institutions;
